import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link2, FileText, Loader2, CheckCircle2, AlertCircle, Upload, BarChart3, Merge, FileWarning, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../AuthContext';
import { processDriveLink, getUpload, listUploads, reprocessUpload, purgeAllData } from '../api';
import { toast } from 'sonner';

const PROCESSING_STEPS = {
  extracting: { label: 'Extracting ZIP', progress: 10 },
  parsing: { label: 'Parsing messages', progress: 25 },
  detecting: { label: 'Detecting issues (AI)', progress: 45 },
  clustering: { label: 'Clustering issues', progress: 65 },
  merging: { label: 'Deduplicating & merging', progress: 80 },
  linking: { label: 'Linking solutions', progress: 90 },
  done: { label: 'Complete', progress: 100 },
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fileUrl, setFileUrl] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [reprocessingId, setReprocessingId] = useState(null);
  const pollRef = useRef(null);

  // Load existing uploads
  useEffect(() => {
    const loadUploads = async () => {
      try {
        const data = await listUploads();
        setUploads(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    };
    loadUploads();
  }, []);

  const handleReprocess = async (uploadId) => {
    if (!window.confirm('This will clear all existing issues for this upload and re-process from scratch. Continue?')) return;
    setReprocessingId(uploadId);
    try {
      await reprocessUpload(uploadId);
      toast.success('Re-processing started!');
      startPolling(uploadId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-process failed');
    } finally {
      setReprocessingId(null);
    }
  };

  const handlePurgeAll = async () => {
    if (!window.confirm('⚠️ This will permanently DELETE all messages, issues, solutions, and uploads. This cannot be undone. Are you sure?')) return;
    if (!window.confirm('FINAL CONFIRMATION: Delete ALL data?')) return;
    try {
      const result = await purgeAllData();
      toast.success(`Purged: ${result.deleted.messages} messages, ${result.deleted.clusters} clusters, ${result.deleted.uploads} uploads`);
      setUploads([]);
      setResult(null);
      setProcessingStatus(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    }
  };

  // Poll upload status for live progress
  const startPolling = useCallback((uploadId) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const upload = await getUpload(uploadId);
        setProcessingStatus(upload);

        if (upload.status === 'completed' || upload.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (upload.status === 'completed') {
            if (upload.duplicate_messages > 0 && upload.new_messages === 0) {
              toast.info('All messages were duplicates — no new issues found.');
            } else {
              toast.success(`Processing complete! ${upload.new_clusters || 0} new + ${upload.merged_clusters || 0} merged clusters.`);
            }
          } else {
            toast.error(`Processing failed: ${upload.error_message || 'Unknown error'}`);
          }
        }
      } catch {
        // Silently retry on poll failure
      }
    }, 3000);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setProcessingStatus(null);

    try {
      const response = await processDriveLink(fileUrl, description || null);
      setResult(response);

      if (response.duplicate) {
        toast.warning('This file has already been processed');
      } else {
        toast.success('File queued for processing!');
        setFileUrl('');
        setDescription('');
        // Start polling for processing status
        if (response.upload_id) {
          startPolling(response.upload_id);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process file');
      setResult({ error: err.response?.data?.detail || 'Failed to process file' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="border-b bg-card">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="admin-page-title">Admin Panel</h1>
            <p className="text-sm text-muted-foreground">Process Google Drive files</p>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Submit Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Process Google Drive Link
                </CardTitle>
                <CardDescription>
                  Paste a Google Drive file link to process WhatsApp chat export
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fileUrl">Google Drive File URL</Label>
                    <Input
                      id="fileUrl"
                      type="url"
                      placeholder="https://drive.google.com/file/d/..."
                      value={fileUrl}
                      onChange={(e) => setFileUrl(e.target.value)}
                      required
                      data-testid="admin-file-url-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Supports: /file/d/FILE_ID or ?id=FILE_ID formats
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Textarea
                      id="description"
                      placeholder="e.g., March 2026 Support Issues"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      data-testid="admin-description-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    data-testid="admin-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Process File
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Result */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Processing Result
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!result ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Submit a file link to see results here</p>
                  </div>
                ) : result.error ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{result.error}</AlertDescription>
                  </Alert>
                ) : result.duplicate ? (
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-900">
                      <p className="font-medium mb-2">File Already Processed</p>
                      <p className="text-sm mb-3">This file has been processed before.</p>
                      <div className="space-y-1 text-xs">
                        <p><strong>Upload ID:</strong> {result.upload_id}</p>
                        <p><strong>Status:</strong> {result.status}</p>
                      </div>
                      <Button
                        size="sm"
                        className="mt-3"
                        onClick={() => navigate(`/dashboard/${result.upload_id}`)}
                      >
                        View Dashboard
                      </Button>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-900">
                      <p className="font-medium mb-2">File Queued Successfully!</p>
                      <p className="text-sm mb-3">{result.message}</p>
                      <div className="space-y-1 text-xs">
                        <p><strong>Upload ID:</strong> {result.upload_id}</p>
                        <p><strong>File ID:</strong> {result.file_id}</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Live Processing Status */}
            {processingStatus && !result?.duplicate && (
              <Card className="mt-6" data-testid="processing-status-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Processing Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {processingStatus.status === 'processing' && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {PROCESSING_STEPS[processingStatus.processing_step]?.label || processingStatus.processing_step}
                        </span>
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      </div>
                      <Progress
                        value={PROCESSING_STEPS[processingStatus.processing_step]?.progress || 0}
                        className="h-2"
                      />
                    </>
                  )}

                  {processingStatus.status === 'completed' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-medium">Processing Complete</span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold" data-testid="stat-total-messages">{processingStatus.total_messages || 0}</p>
                          <p className="text-xs text-muted-foreground">Total Messages</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold text-primary" data-testid="stat-new-messages">{processingStatus.new_messages || 0}</p>
                          <p className="text-xs text-muted-foreground">New Messages</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold text-amber-600" data-testid="stat-duplicates">{processingStatus.duplicate_messages || 0}</p>
                          <p className="text-xs text-muted-foreground">Duplicates Skipped</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold" data-testid="stat-issues">{processingStatus.total_issues || 0}</p>
                          <p className="text-xs text-muted-foreground">Issues Detected</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold text-green-600" data-testid="stat-new-clusters">{processingStatus.new_clusters || 0}</p>
                          <p className="text-xs text-muted-foreground">New Clusters</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Merge className="h-4 w-4 text-blue-600" />
                            <p className="text-2xl font-bold text-blue-600" data-testid="stat-merged-clusters">{processingStatus.merged_clusters || 0}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">Merged Into Existing</p>
                        </div>
                        <div className="rounded-lg border bg-card p-3 text-center">
                          <p className="text-2xl font-bold" data-testid="stat-solutions">{processingStatus.solutions_added || 0}</p>
                          <p className="text-xs text-muted-foreground">Solutions Found</p>
                        </div>
                      </div>

                      {processingStatus.dedup_note && (
                        <Alert>
                          <FileWarning className="h-4 w-4" />
                          <AlertDescription className="text-sm">{processingStatus.dedup_note}</AlertDescription>
                        </Alert>
                      )}

                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => navigate('/dashboard')}
                        data-testid="view-dashboard-btn"
                      >
                        View Dashboard
                      </Button>
                    </div>
                  )}

                  {processingStatus.status === 'failed' && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Processing failed: {processingStatus.error_message || 'Unknown error'}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Instructions */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-base">How It Works</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2 text-muted-foreground">
                <p>1. Share a file from Google Drive and copy the link</p>
                <p>2. Paste the link in the form above</p>
                <p>3. System automatically checks for duplicates</p>
                <p>4. If new, file is downloaded and processed</p>
                <p>5. AI analyzes messages and extracts issues</p>
                <p>6. Issues are enriched with OrgLens employee data</p>
                <p>7. Results appear in the dashboard within minutes</p>
              </CardContent>
            </Card>

            {/* Existing Uploads — Reprocess */}
            {uploads.length > 0 && (
              <Card className="mt-6" data-testid="uploads-list-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Previous Uploads
                  </CardTitle>
                  <CardDescription>Re-process uploads with improved parser</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {uploads.map((upload) => (
                      <div
                        key={upload.id}
                        className="flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{upload.filename || upload.gdrive_file_id || upload.id}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {upload.status}
                            </Badge>
                            <span>{upload.total_messages || 0} messages</span>
                            <span>{upload.total_clusters || 0} clusters</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReprocess(upload._id)}
                          disabled={reprocessingId === upload._id}
                          data-testid={`reprocess-btn-${upload._id}`}
                        >
                          {reprocessingId === upload._id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-1" />
                          )}
                          Reprocess
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Danger Zone: Purge All */}
            <Card className="mt-6 border-red-200" data-testid="danger-zone-card">
              <CardHeader>
                <CardTitle className="text-base text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Permanently delete all processed data — messages, issues, solutions, and uploads. You will need to re-submit files for processing after this.
                </p>
                <Button
                  variant="destructive"
                  onClick={handlePurgeAll}
                  data-testid="purge-all-btn"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Delete All Data
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
